/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { TwoFactorType } from 'models/authentication'
import { useAddTwoFactorMutation, useCreateTwoFactorMutation } from 'api/account'
import { regex } from 'helpers/utils'

import AddTwoFactorCodeForm from 'containers/account/form/addTwoFactorCodeForm'
import CreateTwoFactorVerificationCodeForm from 'containers/account/form/createTwoFactorVerificationCodeForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ConfigureMobileAuthLightbox({ status, close }: Props) {
    const createFormRef = useRef<FormikProps<{ identifier: string }>>(null)
    const addFormRef = useRef<FormikProps<{ code: string }>>(null)
    const [, { isLoading: isCreatingTwoFactor, isSuccess, reset }] = useCreateTwoFactorMutation({ fixedCacheKey: 'create-two-factor-sms' })
    const [, { isLoading: isAddingTwoFactor, isSuccess: isAddingTwoFactorSuccess }] = useAddTwoFactorMutation({ fixedCacheKey: 'add-two-factor-sms' })

    /**********************************************************************************************************
     *   COMPONENTS
     **********************************************************************************************************/
    function Content() {
        const twoFactor = {
            type: 'sms' as TwoFactorType,
            validationSchema: Yup.object({
                identifier: Yup.string()
                    .required('Required')
                    .matches(regex.phone, 'Phone number is not valid')
            }),
            field: {
                label: 'Phone Number',
                type: 'tel'
            }
        }

        if (isSuccess) {
            return <AddTwoFactorCodeForm form={addFormRef} twoFactorType={'sms'} />
        }

        return <CreateTwoFactorVerificationCodeForm form={createFormRef} twoFactor={twoFactor} />
    }

    useEffect(() => {
        if (isAddingTwoFactorSuccess) {
            close()
        }
    }, [isAddingTwoFactorSuccess])

    /**********************************************************************************************************
     *   RENDER
     **********************************************************************************************************/
    return (
        <Lightbox
            title={'Mobile Verification'}
            description={'Your two-factor code will be sent to the phone number below.'}
            content={<Content />}
            conditions={{ state: status, action: () => close() }}
            actions={[
                {
                    label: 'Back',
                    color: 'secondary',
                    type: 'submit',
                    disabled: isCreatingTwoFactor || isAddingTwoFactor,
                    func: () => {
                        close()
                        reset()
                    }
                },
                {
                    label: isSuccess ? 'Add' : 'Continue',
                    color: 'primary',
                    type: 'submit',
                    form: isSuccess ? 'addTwoFactorCodeForm' : 'createTwoFactorVerificationCodeForm',
                    loading: isCreatingTwoFactor || isAddingTwoFactor
                }]
            }
        />
    )
}
